import { Link } from "react-router-dom";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { ArrowRight, Sparkles, BookOpen, Cross, Gift } from "lucide-react";

const ObiecteReligioase = () => {
  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-24 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-accent/10 via-leather/5 to-background -z-10" />
        <div className="absolute top-20 left-10 w-[500px] h-[500px] bg-accent/10 rounded-full blur-3xl -z-10 animate-float" />
        <div className="absolute bottom-10 right-10 w-96 h-96 bg-leather/10 rounded-full blur-3xl -z-10 animate-float" style={{ animationDelay: '1s' }} />
        
        <div className="container mx-auto max-w-5xl text-center">
          <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-accent/10 backdrop-blur-sm border border-accent/20 text-accent mb-8 animate-fade-in">
            <Sparkles className="w-5 h-5" />
            <span className="text-sm font-semibold font-lora">Meșteșug și spiritualitate</span>
          </div>
          
          <h1 className="text-6xl md:text-8xl font-bold mb-8 bg-gradient-to-r from-accent via-primary to-leather bg-clip-text text-transparent animate-fade-in leading-tight font-playfair">
            Obiecte din Piele
            <br />
            <span className="text-5xl md:text-7xl">cu Tematică Religioasă</span>
          </h1>
          
          <p className="text-xl md:text-3xl text-muted-foreground mb-8 leading-relaxed animate-fade-in font-lora max-w-4xl mx-auto" style={{ animationDelay: '0.2s' }}>
            Cadouri inspirate, create manual din piele naturală, care îmbină tradiția meșteșugului românesc cu simbolistica creștină ortodoxă
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-24 px-4 bg-gradient-to-b from-background to-cream/20">
        <div className="container mx-auto max-w-7xl">
          <div className="grid md:grid-cols-2 gap-20 items-center mb-24">
            <div className="relative group animate-fade-in order-2 md:order-1">
              <div className="absolute -inset-6 bg-gradient-to-r from-accent/20 via-leather/20 to-primary/20 rounded-3xl blur-3xl group-hover:blur-[60px] transition-all duration-700 animate-float" />
              <div className="relative bg-card/90 backdrop-blur-sm rounded-3xl p-10 shadow-2xl border-2 border-border/50 hover:border-accent/30 transition-all duration-500">
                <div className="aspect-square bg-gradient-to-br from-accent/10 via-leather/10 to-primary/10 rounded-2xl flex items-center justify-center overflow-hidden relative group-hover:scale-[1.02] transition-transform duration-500">
                  <Cross className="w-40 h-40 text-accent/40 group-hover:text-accent/60 transition-colors duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-leather/20 to-transparent" />
                </div>
              </div>
            </div>

            <div className="space-y-8 animate-fade-in order-1 md:order-2" style={{ animationDelay: '0.3s' }}>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-accent/10 rounded-full border border-accent/20">
                <Gift className="w-5 h-5 text-accent" />
                <span className="font-semibold text-accent font-lora">Cadoul perfect</span>
              </div>
              
              <h2 className="text-5xl md:text-6xl font-bold leading-tight text-primary font-playfair">
                Meșteșug tradițional în serviciul credinței
              </h2>
              
              <p className="text-lg md:text-xl text-muted-foreground leading-relaxed font-lora">
                Obiectele noastre din piele naturală cu tematică religioasă sunt create manual, fiecare purtând amprenta măiestriei artizanale românești. De la semne de carte la coperte pentru cărți de rugăciuni, de la iconiță portabile la cruci decorative - fiecare obiect este conceput să aducă frumusețe și spiritualitate în viața de zi cu zi.
              </p>
              
              <p className="text-lg md:text-xl text-muted-foreground leading-relaxed font-lora">
                Folosim exclusiv piele naturală de cea mai înaltă calitate, prelucrată și decorată prin tehnici tradiționale: gravare, piroguravură, aplicații în relief, aurire. Motivele ornamentale sunt inspirate din arta bizantină și tradiția românească, fiecare detaliu fiind realizat cu grijă și devotament.
              </p>
            </div>
          </div>

          {/* Products Showcase */}
          <div className="mb-24">
            <h2 className="text-5xl md:text-6xl font-bold text-center mb-16 animate-fade-in font-playfair text-primary">
              Colecția noastră
            </h2>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-10">
              {[
                {
                  title: "Semne de carte",
                  description: "Din piele naturală, cu simboluri creștine gravate manual",
                  icon: BookOpen,
                  gradient: "from-accent/10 to-leather/10"
                },
                {
                  title: "Coperte protectoare",
                  description: "Pentru cărți de rugăciuni și psaltiri, personalizabile",
                  icon: BookOpen,
                  gradient: "from-primary/10 to-burgundy/10"
                },
                {
                  title: "Cruci decorative",
                  description: "Realizate din piele sculptată și aurită cu foiță",
                  icon: Cross,
                  gradient: "from-gold/10 to-accent/10"
                },
                {
                  title: "Iconiță portabile",
                  description: "În rame din piele, ideale pentru călătorii",
                  icon: Sparkles,
                  gradient: "from-burgundy/10 to-primary/10"
                }
              ].map((item, index) => (
                <div 
                  key={index}
                  className={`group p-8 rounded-3xl bg-gradient-to-br ${item.gradient} backdrop-blur-sm border-2 border-border/50 hover:border-accent/40 transition-all duration-500 hover:shadow-2xl hover:-translate-y-2 animate-fade-in relative overflow-hidden`}
                  style={{ animationDelay: `${index * 150}ms` }}
                >
                  <div className="absolute top-0 right-0 w-24 h-24 bg-accent/5 rounded-full blur-2xl group-hover:bg-accent/10 transition-colors duration-500" />
                  <div className="relative">
                    <div className="w-20 h-20 rounded-2xl bg-accent/10 flex items-center justify-center mb-6 group-hover:bg-accent/20 transition-all duration-500 group-hover:scale-110">
                      <item.icon className="w-10 h-10 text-accent" />
                    </div>
                    <h3 className="text-2xl font-bold mb-4 group-hover:text-accent transition-colors font-playfair">
                      {item.title}
                    </h3>
                    <p className="text-muted-foreground leading-relaxed text-lg font-lora">
                      {item.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Features Section */}
          <div className="grid md:grid-cols-3 gap-10 mb-24">
            {[
              {
                title: "Piele naturală premium",
                description: "Selectăm cele mai fine pielea naturale, tăbăcite tradițional",
                icon: "🎯",
                gradient: "from-leather/10 to-accent/10"
              },
              {
                title: "Lucrate manual",
                description: "Fiecare obiect este creat de meșteri cu experiență de peste 20 de ani",
                icon: "✋",
                gradient: "from-primary/10 to-burgundy/10"
              },
              {
                title: "Personalizare",
                description: "Puteți comanda obiecte personalizate cu nume, date sau simboluri speciale",
                icon: "⭐",
                gradient: "from-gold/10 to-accent/10"
              }
            ].map((feature, index) => (
              <div 
                key={index}
                className={`text-center p-10 rounded-3xl bg-gradient-to-br ${feature.gradient} backdrop-blur-sm border-2 border-border/50 hover:border-accent/40 transition-all duration-500 hover:shadow-2xl hover:-translate-y-2 animate-fade-in relative overflow-hidden`}
                style={{ animationDelay: `${(index + 4) * 150}ms` }}
              >
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-32 bg-accent/5 rounded-full blur-2xl group-hover:bg-accent/10 transition-colors duration-500" />
                <div className="relative">
                  <div className="text-7xl mb-6 hover:scale-110 transition-transform duration-500">{feature.icon}</div>
                  <h3 className="text-2xl md:text-3xl font-bold mb-4 font-playfair">{feature.title}</h3>
                  <p className="text-muted-foreground leading-relaxed text-lg font-lora">
                    {feature.description}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* CTA Section */}
          <div className="relative rounded-[2rem] overflow-hidden p-16 md:p-24 text-center animate-fade-in bg-gradient-to-br from-accent/10 via-leather/5 to-primary/10 backdrop-blur-sm border-2 border-accent/20">
            <div className="absolute inset-0 bg-gradient-to-br from-accent/20 via-leather/10 to-primary/20 opacity-50 -z-10" />
            <div className="absolute top-10 left-10 w-64 h-64 bg-accent/20 rounded-full blur-3xl animate-float" />
            <div className="absolute bottom-10 right-10 w-64 h-64 bg-leather/20 rounded-full blur-3xl animate-float" style={{ animationDelay: '1s' }} />
            
            <h2 className="text-5xl md:text-6xl font-bold mb-8 text-primary font-playfair relative">
              Cadouri cu suflet și semnificație
            </h2>
            <p className="text-xl md:text-2xl text-muted-foreground mb-10 max-w-3xl mx-auto font-lora leading-relaxed">
              Alegeți un cadou care vorbește despre credință, tradiție și dragoste pentru frumos
            </p>
            
            <div className="flex flex-wrap gap-6 justify-center">
              <Link to="/magazin">
                <Button size="lg" className="text-lg px-10 py-7 rounded-full shadow-2xl hover:shadow-3xl transition-all duration-500 group bg-primary hover:bg-primary/90 hover-scale hover-glow">
                  Explorează Colecția
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
              
              <Link to="/#contact">
                <Button size="lg" variant="outline" className="text-lg px-10 py-7 rounded-full shadow-xl hover:shadow-2xl transition-all duration-500 bg-cream/10 hover:bg-cream/20 backdrop-blur-md border-2 border-accent/30 hover:border-accent/50">
                  Comandă Personalizată
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ObiecteReligioase;
